import { useMemo } from 'react'
import type {
  LlmChatMessageDto,
  LlmChatSessionDto,
} from '@/shared/hooks/api/llmChat'
import {
  isDismissedCatalogRecommendationResolution,
  parseProductUpdateEvent,
} from '@/shared/hooks/api/llmChatProductUpdates'
import {
  type LexProduct,
  useListProducts,
  useProductRecommendations,
} from '@/shared/hooks/api/product'
import {
  type ProductSource,
  useListProductSources,
} from '@/shared/hooks/api/productSource'

type SessionLinkedProductStatus = {
  hasLinkedProduct: boolean
  linkedProductDeleted: boolean
  linkedProductUpdatedAfterLastMessage: boolean
}

type MessageTimestamp = Pick<LlmChatMessageDto, 'createdAt' | 'role' | 'contentJson'>
type ProductTimestamp = Pick<LexProduct, 'updatedAt'>
type ProductSourceTimestamp = Pick<ProductSource, 'createdAt' | 'updatedAt'>
type AcceptedRecommendationTimestamp = {
  status: string
  resolvedAt: string | null
}

/** Product writes can land on the client a few ms before the async chat SYSTEM event. */
const CHAT_PRODUCT_UPDATE_ACK_WINDOW_MS = 15_000

function toTimestamp(value: Date | string | null | undefined): number | null {
  if (value == null) {
    return null
  }
  const time = value instanceof Date ? value.getTime() : Date.parse(value)
  return Number.isFinite(time) ? time : null
}

function isAcknowledgedProductContextSystemMessage(
  message: MessageTimestamp
): boolean {
  return (
    message.role === 'SYSTEM' &&
    parseProductUpdateEvent(message.contentJson) !== null &&
    !isDismissedCatalogRecommendationResolution(message.contentJson)
  )
}

function getLatestNonToolMessageTimestamp(
  messages: ReadonlyArray<MessageTimestamp>
): number | null {
  let latestTimestamp: number | null = null
  for (const message of messages) {
    if (message.role === 'TOOL_RESULT') {
      continue
    }
    if (
      message.role === 'SYSTEM' &&
      !isAcknowledgedProductContextSystemMessage(message)
    ) {
      continue
    }
    const timestamp = toTimestamp(message.createdAt)
    if (timestamp === null) {
      continue
    }
    if (latestTimestamp === null || timestamp > latestTimestamp) {
      latestTimestamp = timestamp
    }
  }
  return latestTimestamp
}

function getLatestAcceptedRecommendationResolvedAt(
  recommendations: ReadonlyArray<AcceptedRecommendationTimestamp>
): number | null {
  let latestTimestamp: number | null = null
  for (const recommendation of recommendations) {
    if (recommendation.status !== 'accepted') {
      continue
    }
    const resolvedAt = toTimestamp(recommendation.resolvedAt)
    if (resolvedAt === null) {
      continue
    }
    if (latestTimestamp === null || resolvedAt > latestTimestamp) {
      latestTimestamp = resolvedAt
    }
  }
  return latestTimestamp
}

function maxTimestamp(
  left: number | null,
  right: number | null
): number | null {
  if (left === null) {
    return right
  }
  if (right === null) {
    return left
  }
  return left > right ? left : right
}

function isProductMetadataUpdateAcknowledged(input: {
  productUpdatedAt: number
  latestBaselineTimestamp: number
  latestAcceptedResolvedAt: number | null
}): boolean {
  if (input.productUpdatedAt <= input.latestBaselineTimestamp) {
    return true
  }
  if (input.latestAcceptedResolvedAt === null) {
    return false
  }
  return (
    Math.abs(input.productUpdatedAt - input.latestAcceptedResolvedAt) <=
    CHAT_PRODUCT_UPDATE_ACK_WINDOW_MS
  )
}

export function isProductContextUpdatedAfterLastMessage(input: {
  product: ProductTimestamp | null | undefined
  sources: ReadonlyArray<ProductSourceTimestamp>
  messages: ReadonlyArray<MessageTimestamp>
  acceptedRecommendations?: ReadonlyArray<AcceptedRecommendationTimestamp>
}): boolean {
  const latestMessageTimestamp = getLatestNonToolMessageTimestamp(input.messages)
  const latestAcceptedResolvedAt = getLatestAcceptedRecommendationResolvedAt(
    input.acceptedRecommendations ?? []
  )
  const latestBaselineTimestamp = maxTimestamp(
    latestMessageTimestamp,
    latestAcceptedResolvedAt
  )
  if (latestBaselineTimestamp === null || !input.product) {
    return false
  }

  const productUpdatedAt = toTimestamp(input.product.updatedAt)
  if (productUpdatedAt !== null) {
    const productMetadataStale = !isProductMetadataUpdateAcknowledged({
      productUpdatedAt,
      latestBaselineTimestamp,
      latestAcceptedResolvedAt,
    })
    if (productMetadataStale) {
      return true
    }
  }

  return input.sources.some((source) => {
    const sourceCreatedAt = toTimestamp(source.createdAt)
    if (
      sourceCreatedAt !== null &&
      sourceCreatedAt > latestBaselineTimestamp
    ) {
      return true
    }
    const sourceUpdatedAt = toTimestamp(source.updatedAt)
    return sourceUpdatedAt !== null && sourceUpdatedAt > latestBaselineTimestamp
  })
}

export function useSessionLinkedProductStatus(
  session: LlmChatSessionDto | null | undefined,
  messages: ReadonlyArray<LlmChatMessageDto> = []
): SessionLinkedProductStatus {
  const productId = session?.productId ?? null
  const sessionId = session?.id ?? null
  const productsQuery = useListProducts()
  const linkedProduct = productsQuery.data?.find(
    (product) => product.id === productId
  )
  const productSourcesQuery = useListProductSources(linkedProduct?.id)
  const recommendationsQuery = useProductRecommendations(
    productId ?? '',
    sessionId ?? ''
  )

  return useMemo(() => {
    const hasLinkedProduct = productId !== null
    const linkedProductDeleted =
      hasLinkedProduct && productsQuery.isSuccess && linkedProduct === undefined
    const linkedProductUpdatedAfterLastMessage =
      hasLinkedProduct &&
      !linkedProductDeleted &&
      isProductContextUpdatedAfterLastMessage({
        product: linkedProduct,
        sources: productSourcesQuery.isSuccess ? productSourcesQuery.data : [],
        messages,
        acceptedRecommendations: recommendationsQuery.data,
      })

    return {
      hasLinkedProduct,
      linkedProductDeleted,
      linkedProductUpdatedAfterLastMessage,
    }
  }, [
    linkedProduct,
    messages,
    productId,
    productSourcesQuery.data,
    productSourcesQuery.isSuccess,
    productsQuery.isSuccess,
    recommendationsQuery.data,
  ])
}
